// src/pages/programs/EditProgram.jsx
// Route: /clubs/:clubId/programs/:programId/edit
import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  collection, getDocs, doc, getDoc, updateDoc, query, where, serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import {
  PlusIcon,
  TrashIcon,
  ChevronLeftIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

// ── constants (same as CreateProgram) ─────────────────────────────────────────
const DOC_OPTIONS = [
  'Birth Certificate', 'Photo ID', 'Medical Release Form', 'Liability Waiver',
  'Insurance Card', 'Proof of Residency', 'School Transcript',
  'Concussion Training Certificate',
];

// ── Waiver templates ──────────────────────────────────────────────────────────
const WAIVER_TEMPLATES = [
  {
    name: 'General Liability Waiver',
    text: `In consideration of my child's participation in [PROGRAM NAME] organized by [ORGANIZATION NAME] ("Organization"), I, as parent or legal guardian, hereby release, waive, and discharge the Organization, its officers, directors, employees, volunteers, and agents from any and all claims, demands, or causes of action arising out of or relating to my child's participation in this program, including claims arising from negligence.

I acknowledge that participation in this sport involves inherent risks of physical injury. I voluntarily accept these risks on behalf of my child and agree to hold harmless the Organization from any injuries, losses, or damages sustained during participation.

I further agree to indemnify the Organization against any claims brought by or on behalf of my child arising from participation in this program.

This release is binding on my heirs, executors, administrators, and successors.

⚠️ CUSTOMIZE THIS TEMPLATE: Replace [ORGANIZATION NAME] and [PROGRAM NAME], and have this waiver reviewed by a licensed attorney before use.`,
  },
  {
    name: 'Medical Authorization Form',
    text: `I, as parent or legal guardian of the above-named player, hereby authorize [ORGANIZATION NAME] and its designated representatives to consent to any emergency medical treatment deemed necessary by a licensed healthcare professional in the event that I cannot be reached.

[ORGANIZATION NAME] will make every effort to contact me before authorizing treatment. I accept financial responsibility for any medical costs incurred.

I certify that, to the best of my knowledge, there are no health conditions that would prevent my child from participating in [PROGRAM NAME], and I have disclosed any relevant medical conditions or special needs to the coaching staff.

⚠️ CUSTOMIZE THIS TEMPLATE: Replace [ORGANIZATION NAME] and [PROGRAM NAME], and have this waiver reviewed by a licensed attorney before use.`,
  },
  {
    name: 'Photo & Video Release',
    text: `I grant [ORGANIZATION NAME] the right to photograph and/or record video of my child during practices, games, and events associated with [PROGRAM NAME]. I authorize the Organization to use these images and recordings for promotional, educational, and non-commercial purposes, including but not limited to social media, websites, printed materials, and press releases.

No compensation will be provided. I understand the images may be used indefinitely and waive any right to inspect or approve the finished materials.

If you do not wish your child to be photographed or filmed, please notify the coaching staff in writing before the program begins.

⚠️ CUSTOMIZE THIS TEMPLATE: Replace [ORGANIZATION NAME] and [PROGRAM NAME], and have this waiver reviewed by a licensed attorney before use.`,
  },
  {
    name: 'Player & Parent Code of Conduct',
    text: `As a condition of participation in [PROGRAM NAME], I agree that my child and I will:

• Treat all players, coaches, officials, and spectators with respect at all times
• Use appropriate language and conduct ourselves in a sportsmanlike manner
• Respect the decisions of referees and coaching staff
• Support all players regardless of ability or performance
• Refrain from any form of bullying, harassment, or discrimination
• Arrive on time to practices and games, or provide advance notice of absences
• Follow all facility rules and organizational policies

I understand that violations of this Code of Conduct may result in suspension or removal from the program without refund.

⚠️ CUSTOMIZE THIS TEMPLATE: Replace [ORGANIZATION NAME] and [PROGRAM NAME] with your organization's details.`,
  },
  {
    name: 'Concussion Acknowledgment',
    text: `I acknowledge that I have read and understand the risks of concussion associated with contact sports. Concussions can occur in any sport or physical activity and may present with symptoms including headache, dizziness, confusion, memory problems, nausea, or sensitivity to light and noise.

I agree that:
• My child will not return to play on the same day a concussion is suspected
• My child will not return to play until cleared in writing by a licensed healthcare provider
• I will inform the coaching staff of any known prior concussions or head injuries
• I will monitor my child for signs of concussion during and after practices and games

I understand that [ORGANIZATION NAME] follows a Return-to-Play protocol consistent with applicable state laws and sporting governing body guidelines.

⚠️ CUSTOMIZE THIS TEMPLATE: Replace [ORGANIZATION NAME] with your organization's name, and consult applicable state concussion laws before use.`,
  },
];

const SPORT_OPTIONS = [
  'Soccer', 'Basketball', 'Baseball', 'Softball', 'Volleyball',
  'Football', 'Lacrosse', 'Hockey', 'Tennis', 'Swimming',
  'Track & Field', 'Wrestling', 'Gymnastics', 'Cross Country', 'Other',
];

const uid = () => crypto.randomUUID();

function makeDivision(clubTeams = []) {
  return {
    id: uid(),
    name: '',
    teamId: clubTeams[0]?.id ?? '',
    teamName: clubTeams[0]?.name ?? '',
    fee: '',
    maxPlayers: '',
  };
}

// Convert Firestore Timestamp / Date / string → YYYY-MM-DD for <input type="date">
function toInputDate(v) {
  if (!v) return '';
  let d;
  if (v?.toDate) d = v.toDate();
  else if (v?.seconds) d = new Date(v.seconds * 1000);
  else d = new Date(v);
  if (isNaN(d.getTime())) return '';
  return format(d, 'yyyy-MM-dd');
}

const inputCls =
  'block w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-[#0d1f14] placeholder:text-zinc-400 focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30 transition-all';

function Section({ title, children, action }) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p
          className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
          style={{ fontFamily: HEAD_FONT }}
        >
          {title}
        </p>
        {action}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children, hint, required, small }) {
  return (
    <div className="space-y-1.5">
      <label className={`block font-semibold text-[#0d1f14] ${small ? 'text-xs' : 'text-sm'}`}>
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-zinc-400">{hint}</p>}
    </div>
  );
}

// ── main ───────────────────────────────────────────────────────────────────────
export default function EditProgram() {
  const { clubId, programId } = useParams();
  const navigate = useNavigate();

  const [program, setProgram] = useState(null);
  const [clubTeams, setClubTeams] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);

  // Form state
  const [name, setName] = useState('');
  const [sport, setSport] = useState('');
  const [description, setDescription] = useState('');
  const [defaultFee, setDefaultFee] = useState('');
  const [earlyBirdFee, setEarlyBirdFee] = useState('');
  const [earlyBirdDeadline, setEarlyBirdDeadline] = useState('');
  // Processing fee: 'absorb' (coach pays Stripe fee) | 'pass_to_parent' (parent pays)
  const [processingFeeMode, setProcessingFeeMode] = useState('absorb');
  const [regOpen, setRegOpen] = useState('');
  const [regClose, setRegClose] = useState('');
  const [divisions, setDivisions] = useState([]);
  const [requiredDocTypes, setRequiredDocTypes] = useState([]);
  const [waivers, setWaivers] = useState([]);
  const [extraFields, setExtraFields] = useState([]);
  const [saving, setSaving] = useState(false);

  // ── load program + club teams ────────────────────────────────────────────────
  useEffect(() => {
    if (!programId || !clubId) return;
    (async () => {
      try {
        const [progSnap, teamsSnap] = await Promise.all([
          getDoc(doc(db, 'programs', programId)),
          getDocs(query(collection(db, 'teams'), where('clubId', '==', clubId))),
        ]);

        if (!progSnap.exists()) { toast.error('Program not found'); return; }
        const p = { id: progSnap.id, ...progSnap.data() };
        setProgram(p);

        const teams = teamsSnap.docs
          .map((d) => ({ id: d.id, name: d.data().name || d.id }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setClubTeams(teams);

        // Pre-populate form
        setName(p.name ?? '');
        setSport(p.sport ?? '');
        setDescription(p.description ?? '');
        setDefaultFee(p.defaultFee > 0 ? (p.defaultFee / 100).toFixed(2) : '');
        setEarlyBirdFee(p.earlyBirdFee > 0 ? (p.earlyBirdFee / 100).toFixed(2) : '');
        setEarlyBirdDeadline(toInputDate(p.earlyBirdDeadline));
        setProcessingFeeMode(p.processingFeeMode === 'pass_to_parent' ? 'pass_to_parent' : 'absorb');
        setRegOpen(toInputDate(p.registrationOpen));
        setRegClose(toInputDate(p.registrationClose));
        setRequiredDocTypes(p.requiredDocTypes ?? []);
        setWaivers((p.waivers ?? []).map((w) => ({ ...w, id: w.id ?? uid() })));
        setExtraFields(p.extraFields ?? []);

        // Divisions: cents → dollars for display
        setDivisions(
          (p.divisions ?? []).map((d) => ({
            ...d,
            fee: d.fee != null ? (d.fee / 100).toFixed(2) : '',
            maxPlayers: d.maxPlayers != null ? String(d.maxPlayers) : '',
          }))
        );
      } catch (e) {
        console.error('[EditProgram] load error:', e);
        toast.error('Failed to load program');
      } finally {
        setPageLoading(false);
      }
    })();
  }, [programId, clubId]);

  // ── division helpers ─────────────────────────────────────────────────────────
  const addDivision = () =>
    setDivisions((ds) => [...ds, makeDivision(clubTeams)]);

  const removeDivision = (id) =>
    setDivisions((ds) => ds.filter((d) => d.id !== id));

  const updateDivision = (id, field, value) =>
    setDivisions((ds) =>
      ds.map((d) => {
        if (d.id !== id) return d;
        if (field === 'teamId') {
          const team = clubTeams.find((t) => t.id === value);
          return { ...d, teamId: value, teamName: team?.name ?? '' };
        }
        return { ...d, [field]: value };
      })
    );

  // ── doc + waiver helpers ─────────────────────────────────────────────────────
  const toggleDoc = (docOpt) =>
    setRequiredDocTypes((prev) =>
      prev.includes(docOpt) ? prev.filter((d) => d !== docOpt) : [...prev, docOpt]
    );

  const addWaiver = () =>
    setWaivers((ws) => [...ws, { id: uid(), name: '', text: '' }]);
  const addWaiverFromTemplate = (tpl) =>
    setWaivers((ws) => [...ws, { id: uid(), name: tpl.name, text: tpl.text }]);
  const removeWaiver = (id) =>
    setWaivers((ws) => ws.filter((w) => w.id !== id));
  const updateWaiver = (id, field, value) =>
    setWaivers((ws) => ws.map((w) => (w.id === id ? { ...w, [field]: value } : w)));

  // ── extra field helpers ───────────────────────────────────────────────────────
  const addExtraField = () =>
    setExtraFields((fs) => [...fs, { id: uid(), label: '', type: 'text', required: false, options: [] }]);
  const removeExtraField = (id) =>
    setExtraFields((fs) => fs.filter((f) => f.id !== id));
  const updateExtraField = (id, field, value) =>
    setExtraFields((fs) => fs.map((f) => (f.id === id ? { ...f, [field]: value } : f)));

  // ── submit ───────────────────────────────────────────────────────────────────
  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Program name is required'); return; }

    const cleanDivisions = divisions.map((d) => ({
      id: d.id,
      name: d.name.trim() || 'Division',
      teamId: d.teamId,
      teamName: d.teamName,
      fee: d.fee !== '' ? Math.round(Number(d.fee) * 100) : null,
      maxPlayers: d.maxPlayers !== '' ? Number(d.maxPlayers) : null,
    }));

    try {
      setSaving(true);
      await updateDoc(doc(db, 'programs', programId), {
        name: name.trim(),
        sport: sport.trim(),
        description: description.trim(),
        defaultFee: defaultFee !== '' ? Math.round(Number(defaultFee) * 100) : 0,
        earlyBirdFee: earlyBirdFee !== '' ? Math.round(Number(earlyBirdFee) * 100) : 0,
        earlyBirdDeadline: earlyBirdDeadline ? new Date(earlyBirdDeadline) : null,
        processingFeeMode,
        registrationOpen: regOpen ? new Date(regOpen) : null,
        registrationClose: regClose ? new Date(regClose) : null,
        divisions: cleanDivisions,
        requiredDocTypes,
        waivers: waivers.map(({ id, name: wn, text }) => ({ id, name: wn, text })),
        extraFields: extraFields.map(({ id, label, type, required, options }) => ({ id, label, type, required: !!required, options: options ?? [] })),
        updatedAt: serverTimestamp(),
      });
      toast.success('Program updated');
      navigate(`/clubs/${clubId}/programs/${programId}`);
    } catch (err) {
      console.error('[EditProgram] save error:', err);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // ── loading / not found ──────────────────────────────────────────────────────
  if (pageLoading) {
    return (
      <div className="min-h-screen bg-[#f7faf5]">
        <div className="flex items-center justify-center py-24 text-zinc-400 text-sm">
          Loading program…
        </div>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="min-h-screen bg-[#f7faf5]">
        <div className="text-center py-24">
          <p className="text-zinc-500">Program not found.</p>
          <Link to={`/clubs/${clubId}/programs`} className="text-[#5a8a1f] hover:text-[#3d6010] underline mt-2 inline-block text-sm">
            Back to Programs
          </Link>
        </div>
      </div>
    );
  }

  // ── render ───────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#f7faf5]">
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6 pb-16">
      <Link
        to={`/clubs/${clubId}/programs/${programId}`}
        className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-[#5a8a1f] transition-colors"
      >
        <ChevronLeftIcon className="h-4 w-4" />
        Back to Program
      </Link>

      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#5a8a1f]" style={{ fontFamily: HEAD_FONT }}>
          Program
        </p>
        <h1 className="mt-1 text-2xl font-bold uppercase tracking-tight text-[#0d1f14]" style={{ fontFamily: HEAD_FONT }}>
          Edit Program
        </h1>
        <p className="text-sm text-zinc-500 mt-1">{program.name}</p>
      </div>

      <form onSubmit={handleSave} className="space-y-6">

        {/* ── Program Info ──────────────────────────────────────────────────── */}
        <Section title="Program Info">
          <Field label="Program Name" required>
            <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} required />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Sport">
              <select className={inputCls} value={sport} onChange={(e) => setSport(e.target.value)}>
                <option value="">Select sport…</option>
                {SPORT_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Description">
            <textarea
              className={`${inputCls} min-h-[80px] resize-y`}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </Field>
        </Section>

        {/* ── Registration Window ───────────────────────────────────────────── */}
        <Section title="Registration Window">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Opens">
              <input type="date" className={inputCls} value={regOpen} onChange={(e) => setRegOpen(e.target.value)} />
            </Field>
            <Field label="Closes">
              <input type="date" className={inputCls} value={regClose} onChange={(e) => setRegClose(e.target.value)} />
            </Field>
          </div>
        </Section>

        {/* ── Pricing ───────────────────────────────────────────────────────── */}
        <Section title="Pricing">
          <Field label="Default Registration Fee ($)" hint="Per player. Division fees override this.">
            <input
              type="number" min="0" step="0.01" className={inputCls}
              value={defaultFee} onChange={(e) => setDefaultFee(e.target.value)}
            />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Early Bird Fee ($)" hint="Leave blank to skip">
              <input
                type="number" min="0" step="0.01" className={inputCls}
                value={earlyBirdFee} onChange={(e) => setEarlyBirdFee(e.target.value)}
              />
            </Field>
            <Field label="Early Bird Deadline">
              <input type="date" className={inputCls} value={earlyBirdDeadline} onChange={(e) => setEarlyBirdDeadline(e.target.value)} />
            </Field>
          </div>

          {/* Processing fees */}
          <div className="mt-6 pt-6 border-t border-zinc-200">
            <p
              className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-1"
              style={{ fontFamily: HEAD_FONT }}
            >
              Processing Fees
            </p>
            <p className="text-xs text-zinc-500 mt-0.5 mb-3">
              Stripe charges 2.9% + $0.30 per transaction. Choose who pays it.
            </p>
            <div className="space-y-2">
              <label className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                processingFeeMode === 'pass_to_parent'
                  ? 'border-[#b5f03d] bg-[#b5f03d]/10'
                  : 'border-zinc-200 bg-white hover:border-zinc-300'
              }`}>
                <input
                  type="radio"
                  name="processingFeeMode"
                  value="pass_to_parent"
                  checked={processingFeeMode === 'pass_to_parent'}
                  onChange={() => setProcessingFeeMode('pass_to_parent')}
                  className="mt-0.5 h-4 w-4 accent-[#b5f03d]"
                />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-[#0d1f14]">
                    Parent pays the fee <span className="text-xs font-normal text-[#5a8a1f]">· Recommended</span>
                  </p>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    A small processing fee is added at checkout so you receive the full registration price.
                    Example: on a $100 registration, parent pays ~$103.30.
                  </p>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                processingFeeMode === 'absorb'
                  ? 'border-[#b5f03d] bg-[#b5f03d]/10'
                  : 'border-zinc-200 bg-white hover:border-zinc-300'
              }`}>
                <input
                  type="radio"
                  name="processingFeeMode"
                  value="absorb"
                  checked={processingFeeMode === 'absorb'}
                  onChange={() => setProcessingFeeMode('absorb')}
                  className="mt-0.5 h-4 w-4 accent-[#b5f03d]"
                />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-[#0d1f14]">I'll absorb the fee</p>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    Parents pay exactly the registration price. Stripe's fee is deducted from your payout.
                  </p>
                </div>
              </label>
            </div>
          </div>
        </Section>

        {/* ── Divisions ─────────────────────────────────────────────────────── */}
        <Section
          title="Divisions"
          action={
            clubTeams.length > 0 ? (
              <button type="button" onClick={addDivision}
                className="inline-flex items-center gap-1 text-sm font-bold uppercase text-[#5a8a1f] hover:text-[#3d6010] transition-colors"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}>
                <PlusIcon className="h-4 w-4" /> Add Division
              </button>
            ) : null
          }
        >
          {clubTeams.length === 0 && (
            <div className="flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-200 p-4">
              <InformationCircleIcon className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700">No teams found in this club.</p>
            </div>
          )}

          <div className="space-y-3">
            {divisions.map((div, i) => (
              <div key={div.id} className="rounded-xl border border-zinc-200 bg-zinc-50 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span
                    className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
                    style={{ fontFamily: HEAD_FONT }}
                  >
                    Division {i + 1}
                  </span>
                  <button type="button" onClick={() => removeDivision(div.id)}
                    className="text-zinc-400 hover:text-red-500 transition-colors">
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Division Name" small>
                    <input className={inputCls} placeholder="e.g. U10 Boys" value={div.name}
                      onChange={(e) => updateDivision(div.id, 'name', e.target.value)} />
                  </Field>
                  <Field label="Assign to Team" small>
                    <select className={inputCls} value={div.teamId}
                      onChange={(e) => updateDivision(div.id, 'teamId', e.target.value)}>
                      <option value="">Select team…</option>
                      {clubTeams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </Field>
                  <Field label="Fee Override ($)" hint="Blank = default" small>
                    <input type="number" min="0" step="0.01" className={inputCls} placeholder="Default"
                      value={div.fee} onChange={(e) => updateDivision(div.id, 'fee', e.target.value)} />
                  </Field>
                  <Field label="Max Players" hint="Optional" small>
                    <input type="number" min="1" className={inputCls} placeholder="No limit"
                      value={div.maxPlayers} onChange={(e) => updateDivision(div.id, 'maxPlayers', e.target.value)} />
                  </Field>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* ── Required Documents ────────────────────────────────────────────── */}
        <Section title="Required Documents">
          <div className="flex flex-wrap gap-2">
            {DOC_OPTIONS.map((docOpt) => {
              const active = requiredDocTypes.includes(docOpt);
              return (
                <button key={docOpt} type="button" onClick={() => toggleDoc(docOpt)}
                  className={`rounded-full px-3 py-1.5 text-xs font-bold uppercase border transition-colors ${
                    active
                      ? 'bg-[#0d1f14] text-white border-[#0d1f14]'
                      : 'bg-white text-zinc-600 border-zinc-200 hover:border-[#b5f03d]/60 hover:text-[#5a8a1f]'
                  }`}
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}>
                  {docOpt}
                </button>
              );
            })}
          </div>
        </Section>

        {/* ── Waivers ───────────────────────────────────────────────────────── */}
        <Section
          title="Waivers"
          action={
            <div className="flex items-center gap-2">
              <select
                className="text-sm border border-zinc-200 rounded-lg px-2 py-1 text-[#0d1f14] bg-white focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30"
                defaultValue=""
                onChange={(e) => {
                  if (!e.target.value) return;
                  const tpl = WAIVER_TEMPLATES.find(t => t.name === e.target.value);
                  if (tpl) addWaiverFromTemplate(tpl);
                  e.target.value = '';
                }}
              >
                <option value="">📋 Use a template…</option>
                {WAIVER_TEMPLATES.map(t => (
                  <option key={t.name} value={t.name}>{t.name}</option>
                ))}
              </select>
              <button type="button" onClick={addWaiver}
                className="inline-flex items-center gap-1 text-sm font-bold uppercase text-[#5a8a1f] hover:text-[#3d6010] transition-colors"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}>
                <PlusIcon className="h-4 w-4" /> Blank
              </button>
            </div>
          }
        >
          {/* Legal disclaimer — stronger, explicit that templates are samples only */}
          <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4 mb-4">
            <p className="text-sm font-bold text-amber-900 mb-1">⚠️ Important — These templates are examples only.</p>
            <p className="text-sm text-amber-900 leading-relaxed">
              Any waiver text shown here (including the templates in the dropdown above) is provided as a
              <strong> sample only</strong>. You must review and customize every waiver to reflect your
              organization's specific rules, your state's laws, your league's requirements, and the activities
              involved. Scrimly is a registration platform — we do not provide legal advice and are not
              responsible for the legal sufficiency or enforceability of your waiver language.{' '}
              <strong>Consult a licensed attorney</strong> before relying on any waiver for legal protection.
            </p>
          </div>

          {waivers.length === 0 && (
            <p className="text-sm text-zinc-400">No waivers. Families won't need to sign anything.</p>
          )}
          <div className="space-y-4">
            {waivers.map((w, i) => (
              <div key={w.id} className="rounded-xl border border-zinc-200 bg-zinc-50 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span
                    className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
                    style={{ fontFamily: HEAD_FONT }}
                  >
                    Waiver {i + 1}
                  </span>
                  <button type="button" onClick={() => removeWaiver(w.id)}
                    className="text-zinc-400 hover:text-red-500 transition-colors">
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
                <Field label="Waiver Title" small>
                  <input className={inputCls} placeholder="e.g. Liability Waiver" value={w.name}
                    onChange={(e) => updateWaiver(w.id, 'name', e.target.value)} />
                </Field>
                <Field label="Waiver Text" small>
                  <textarea className={`${inputCls} min-h-[100px] resize-y`} rows={4}
                    value={w.text} onChange={(e) => updateWaiver(w.id, 'text', e.target.value)} />
                  <p className="text-[11px] text-zinc-400 mt-1">
                    This text is set by your organization. Review and customize it — Scrimly does not
                    verify or take responsibility for its legal sufficiency.
                  </p>
                </Field>
              </div>
            ))}
          </div>
        </Section>

        {/* ── Extra Registration Fields ─────────────────────────────────────── */}
        <Section
          title="Extra Registration Fields"
          action={
            <button
              type="button"
              onClick={addExtraField}
              className="inline-flex items-center gap-1 text-sm font-bold uppercase text-[#5a8a1f] hover:text-[#3d6010] transition-colors"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              <PlusIcon className="h-4 w-4" />
              Add Field
            </button>
          }
        >
          {extraFields.length === 0 && (
            <p className="text-sm text-zinc-400">
              Optional: add custom questions like jersey size, emergency contact, or returning player status.
            </p>
          )}
          <div className="space-y-4">
            {extraFields.map((f, i) => (
              <div key={f.id} className="rounded-xl border border-zinc-200 bg-zinc-50 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span
                    className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
                    style={{ fontFamily: HEAD_FONT }}
                  >
                    Field {i + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeExtraField(f.id)}
                    className="text-zinc-400 hover:text-red-500 transition-colors"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Label" small>
                    <input
                      className={inputCls}
                      placeholder="e.g. Jersey Size"
                      value={f.label}
                      onChange={(e) => updateExtraField(f.id, 'label', e.target.value)}
                    />
                  </Field>

                  <Field label="Type" small>
                    <select
                      className={inputCls}
                      value={f.type}
                      onChange={(e) => updateExtraField(f.id, 'type', e.target.value)}
                    >
                      <option value="text">Short Text</option>
                      <option value="textarea">Long Text</option>
                      <option value="select">Dropdown</option>
                      <option value="checkbox">Yes / No checkbox</option>
                    </select>
                  </Field>
                </div>

                {f.type === 'select' && (
                  <Field label="Options (one per line)" small>
                    <textarea
                      className={`${inputCls} min-h-[72px] resize-y`}
                      placeholder="XS&#10;S&#10;M&#10;L&#10;XL"
                      value={(f.options ?? []).join('\n')}
                      onChange={(e) =>
                        updateExtraField(f.id, 'options', e.target.value.split('\n').map(s => s.trim()).filter(Boolean))
                      }
                      rows={3}
                    />
                  </Field>
                )}

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!f.required}
                    onChange={(e) => updateExtraField(f.id, 'required', e.target.checked)}
                    className="h-4 w-4 rounded border-zinc-300 accent-[#b5f03d]"
                  />
                  <span className="text-sm text-[#0d1f14]">Required field</span>
                </label>
              </div>
            ))}
          </div>
        </Section>

        {/* ── Actions ───────────────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-4 border-t border-zinc-100">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-6 py-3 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97] disabled:opacity-50"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
          <Link
            to={`/clubs/${clubId}/programs/${programId}`}
            className="text-sm text-zinc-500 hover:text-[#5a8a1f] text-center py-3 transition-colors"
          >
            Cancel
          </Link>
        </div>
      </form>
      </div>
    </div>
  );
}
